import { useEffect, useState } from "react";
import { RELAYER_URL } from "./env";

export interface PoolStats {
  pool: string;
  denomination: string;
  deposits: number;
  withdrawals: number;
  anonymitySetSize: number;
  lastDepositTimestamp: number | null;
  lastWithdrawalTimestamp: number | null;
  tvlNano: string;
}

export interface NetworkStats {
  network: string;
  totals: {
    deposits: number;
    withdrawals: number;
    anonymitySetSize: number;
    tvlNano: string;
    activePoolCount: number;
  };
  pools: Record<string, PoolStats>;
  cachedAt: number;
}

const POLL_INTERVAL_MS = 15_000;

// Module-level singleton: every component that calls useNetworkStats shares
// one polling loop, one fetch, one cached payload. Mounting a third panel
// shouldn't triple the relayer load.
let cached: NetworkStats | null = null;
let lastFetchAt = 0;
let inFlight: Promise<NetworkStats | null> | null = null;
const listeners = new Set<(s: NetworkStats | null) => void>();
let pollerStarted = false;

async function fetchOnce(): Promise<NetworkStats | null> {
  if (inFlight) return inFlight;
  const base = RELAYER_URL.replace(/\/+$/, "");
  inFlight = fetch(`${base}/stats`, { cache: "no-cache" })
    .then((r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json() as Promise<NetworkStats>;
    })
    .then((data) => {
      cached = data;
      lastFetchAt = Date.now();
      listeners.forEach((cb) => cb(cached));
      return cached;
    })
    .catch((err) => {
      console.warn("[useStats] fetch failed:", err);
      return cached;
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

function ensurePoller() {
  if (pollerStarted) return;
  pollerStarted = true;
  fetchOnce();
  setInterval(() => {
    // Avoid stacking polls if a prior request is still in flight or if
    // the tab was backgrounded long enough for setInterval drift.
    if (!inFlight && Date.now() - lastFetchAt >= POLL_INTERVAL_MS - 500) {
      fetchOnce();
    }
  }, POLL_INTERVAL_MS);
  // Refresh immediately when the tab regains focus — common case is a
  // user switching back to the dApp after a deposit confirmed elsewhere.
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") fetchOnce();
    });
  }
}

export function useNetworkStats(): NetworkStats | null {
  const [stats, setStats] = useState<NetworkStats | null>(cached);
  useEffect(() => {
    ensurePoller();
    listeners.add(setStats);
    if (cached) setStats(cached);
    return () => {
      listeners.delete(setStats);
    };
  }, []);
  return stats;
}

export function usePoolStats(poolKey: string): PoolStats | null {
  const network = useNetworkStats();
  return network?.pools[poolKey] ?? null;
}
